// Lógica pura da tela de mensagens agendadas: fuso, texto da recorrência e
// leitura das listas de destinatários. Fora daqui fica só JSX.

export const DIAS_SEMANA = [
  { valor: 0, curto: 'Dom', longo: 'Domingo' },
  { valor: 1, curto: 'Seg', longo: 'Segunda' },
  { valor: 2, curto: 'Ter', longo: 'Terça' },
  { valor: 3, curto: 'Qua', longo: 'Quarta' },
  { valor: 4, curto: 'Qui', longo: 'Quinta' },
  { valor: 5, curto: 'Sex', longo: 'Sexta' },
  { valor: 6, curto: 'Sáb', longo: 'Sábado' }
]

export const FUSO_PADRAO = 'America/Sao_Paulo'

// Quanto o fuso está deslocado do UTC, em minutos, NAQUELE instante — a conta
// respeita horário de verão porque pergunta ao Intl na data certa.
function offsetMinutos(timezone, data) {
  const formatador = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  })
  const p = formatador.formatToParts(data).reduce((acc, parte) => {
    acc[parte.type] = parte.value
    return acc
  }, {})
  // 'en-US' com hour12:false devolve 24 para meia-noite; Date.UTC espera 0.
  const hora = Number(p.hour) === 24 ? 0 : Number(p.hour)
  const comoSeFosseUTC = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    hora, Number(p.minute), Number(p.second)
  )
  return (comoSeFosseUTC - data.getTime()) / 60000
}

// O instante exato em que, NO FUSO INFORMADO, o relógio marca data + hora.
// Duas passadas: a primeira estima o offset, a segunda corrige quando a data
// cai justamente na virada do horário de verão.
export function instanteNoFuso(data, hora, timezone = FUSO_PADRAO) {
  const [ano, mes, dia] = String(data).split('-').map(Number)
  const [h, min] = String(hora).split(':').map(Number)
  const base = Date.UTC(ano, mes - 1, dia, h, min, 0)

  let instante = base
  for (let i = 0; i < 2; i += 1) {
    instante = base - offsetMinutos(timezone, new Date(instante)) * 60000
  }
  return new Date(instante)
}

// startAt para o backend. Vai como MEIO-DIA local de propósito: o master lê o
// DIA desse campo no fuso do cliente, e nenhum fuso do mundo empurra o meio-dia
// para outro dia. Mandar meia-noite UTC, por exemplo, faria o dia 19 chegar lá
// como dia 18 em Brasília — e o agendamento sairia um dia antes.
export function montarStartAt(data, timezone = FUSO_PADRAO) {
  return instanteNoFuso(data, '12:00', timezone)
}

function listarDias(valores) {
  const nomes = DIAS_SEMANA
    .filter((d) => valores.includes(d.valor))
    .map((d) => d.curto.toLowerCase())
  if (nomes.length === 1) return nomes[0]
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
}

// A frase que o cliente lê. Nunca expõe cron nem jargão de agendador.
export function descreverRecorrencia(schedule = {}) {
  const { kind, time, weekDays = [], monthDay, intervalDays } = schedule
  const hora = time || '00:00'

  switch (kind) {
    case 'once':
      return `Uma vez, às ${hora}`
    case 'daily':
      return `Todo dia às ${hora}`
    case 'weekly':
      if (!weekDays.length) return 'Escolha ao menos um dia da semana'
      return `Toda ${listarDias(weekDays)} às ${hora}`
    case 'monthly':
      return `Todo dia ${monthDay || 1} do mês às ${hora}`
    case 'everyNDays':
      return `A cada ${intervalDays || 1} dias às ${hora}`
    default:
      return 'Recorrência não definida'
  }
}

// Só dígitos, com o 55 completado quando o cliente digita no formato nacional.
// Devolve null quando não dá para aproveitar — quem chama decide o que dizer.
function normalizarTelefone(bruto) {
  const digitos = String(bruto || '').replace(/\D/g, '')
  if (!digitos) return null
  const comPais = digitos.length === 10 || digitos.length === 11 ? `55${digitos}` : digitos
  return comPais.length >= 12 && comPais.length <= 15 ? comPais : null
}

// Quebra uma linha em telefone + nome. Aceita as duas formas que o cliente usa
// na prática: só o número, ou "número, nome".
//
// A desambiguação importa: "5511999998888, 5511977776666" são DOIS
// destinatários, enquanto "5511999998888, Marcos" é um só com nome. A regra é
// olhar o segundo campo — se ele também é telefone válido, são dois números.
// O split é no PRIMEIRO separador apenas, senão "Silva, Marcos" perderia o
// sobrenome.
function lerLinha(linha) {
  const separador = linha.match(/[,;\t]/)
  if (!separador) return [{ bruto: linha }]

  const corte = linha.indexOf(separador[0])
  const primeiro = linha.slice(0, corte).trim()
  const resto = linha.slice(corte + 1).trim()

  if (!resto) return [{ bruto: primeiro }]

  // Segundo campo é telefone? Então a linha traz vários números.
  if (normalizarTelefone(resto.split(/[,;\t]/)[0])) {
    return [{ bruto: primeiro }, ...resto.split(/[,;\t]/).map((p) => ({ bruto: p.trim() }))]
  }

  return [{ bruto: primeiro, nome: resto }]
}

// Números colados: um por linha (com nome opcional depois de vírgula), ou
// vários números na mesma linha separados por vírgula/ponto e vírgula.
export function parsearNumeros(texto) {
  const linhas = String(texto || '')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)

  const validos = []
  const invalidos = []
  const vistos = new Set()

  linhas.forEach((linha) => {
    lerLinha(linha).forEach(({ bruto, nome }) => {
      if (!bruto) return
      const telefone = normalizarTelefone(bruto)
      if (!telefone) {
        invalidos.push(bruto)
        return
      }
      if (vistos.has(telefone)) return
      vistos.add(telefone)

      const destinatario = { phone: telefone }
      if (nome) destinatario.name = nome
      validos.push(destinatario)
    })
  })

  return { validos, invalidos }
}

// Divide uma linha de CSV respeitando aspas — "R$ 129,90" tem vírgula dentro e
// não pode virar duas colunas.
function dividirLinha(linha, separador) {
  const campos = []
  let atual = ''
  let dentroDeAspas = false

  for (let i = 0; i < linha.length; i += 1) {
    const c = linha[i]
    if (c === '"') {
      dentroDeAspas = !dentroDeAspas
    } else if (c === separador && !dentroDeAspas) {
      campos.push(atual.trim())
      atual = ''
    } else {
      atual += c
    }
  }
  campos.push(atual.trim())
  return campos
}

const COLUNAS_TELEFONE = ['telefone', 'phone', 'celular', 'whatsapp', 'numero']
const COLUNAS_NOME = ['nome', 'name', 'cliente']

// Cabeçalho da planilha vira nome de coluna simples (letras sem acento,
// números e "_"): é o único formato que o envio sabe preencher em {{coluna}}.
// "Data Vencimento" → data_vencimento, "Situação" → situacao, "Nº." → no.
// Antes o nome ia cru e o cliente final recebia "{{data vencimento}}" literal.
export function nomeDaColuna(cabecalho, posicao = 0) {
  const nome = String(cabecalho || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return nome || `coluna_${posicao + 1}`
}

// Dois cabeçalhos que dariam o mesmo nome ganham sufixo: valor, valor_2…
function nomesSemRepetir(cabecalhos) {
  const vistos = new Map()
  return cabecalhos.map((c, i) => {
    const base = nomeDaColuna(c, i)
    const vezes = (vistos.get(base) || 0) + 1
    vistos.set(base, vezes)
    return vezes === 1 ? base : `${base}_${vezes}`
  })
}

// Lê a planilha: a coluna de telefone identifica o destinatário, a de nome vira
// {{nome}}, e TODAS as outras viram variáveis disponíveis na mensagem.
export function parsearCSV(conteudo) {
  const linhas = String(conteudo || '').split(/\r?\n/).filter((l) => l.trim())
  if (!linhas.length) return { erro: 'A planilha está vazia' }

  const separador = linhas[0].includes(';') ? ';' : ','
  const colunas = nomesSemRepetir(dividirLinha(linhas[0], separador))

  const idxTelefone = colunas.findIndex((c) => COLUNAS_TELEFONE.includes(c))
  if (idxTelefone === -1) {
    return {
      erro: `Não achei a coluna de telefone. Nomeie uma coluna como "telefone" (ou ${COLUNAS_TELEFONE.slice(1).join(', ')}).`
    }
  }
  const idxNome = colunas.findIndex((c) => COLUNAS_NOME.includes(c))

  const variaveis = colunas.filter((_, i) => i !== idxTelefone && i !== idxNome)

  const validos = []
  const invalidos = []
  const vistos = new Set()

  linhas.slice(1).forEach((linha, i) => {
    const campos = dividirLinha(linha, separador)
    const telefone = normalizarTelefone(campos[idxTelefone])

    if (!telefone) {
      invalidos.push({ linha: i + 2, valor: campos[idxTelefone] || '' })
      return
    }
    if (vistos.has(telefone)) return
    vistos.add(telefone)

    const vars = {}
    colunas.forEach((coluna, idx) => {
      if (idx === idxTelefone || idx === idxNome) return
      if (campos[idx] !== undefined && campos[idx] !== '') vars[coluna] = campos[idx]
    })

    const destinatario = { phone: telefone, vars }
    if (idxNome !== -1 && campos[idxNome]) destinatario.name = campos[idxNome]
    validos.push(destinatario)
  })

  return { colunas, variaveis, validos, invalidos }
}

// ---------------------------------------------------------------------------
// Template Meta Oficial
//
// Em conexão Oficial a mensagem só pode sair por template aprovado, e o
// secondary descobre qual template usar lendo META-TAGS dentro do próprio
// texto. Então o agendamento não guarda "a mensagem": guarda o snippet.
//
// O formato é o do parser (secondary/src/utils/metaTag.js) e é POSICIONAL —
// um %VariableNumeric% por {{n}} do corpo, na ordem, e um %ButtonUrl% por
// botão de URL, também na ordem. Trocar a ordem troca os valores de lugar sem
// erro nenhum, então isto aqui é copiado do buildSendSnippet do módulo de
// templates, mudando só uma coisa: os valores são os que o CLIENTE escolheu,
// não os exemplos.
//
// Os valores podem conter {{nome}}, {{valor}} e afins — o worker resolve por
// destinatário ANTES de o secondary parsear as meta-tags, então cada pessoa
// recebe o template preenchido com os dados dela.
// ---------------------------------------------------------------------------
const TOKEN_LINK_PAGAMENTO = '{{PAYMENT_LINK_CODE}}'

function acharComponente(components, tipo) {
  return (Array.isArray(components) ? components : [])
    .find((c) => String(c?.type || '').toUpperCase() === tipo)
}

// Um botão de URL é "dinâmico de pagamento" quando aponta pro hiperpay E tem
// variável. Só o host hiperpay vale como sinal: uma URL de rastreio também tem
// {{n}} e receberia o token de pagamento por engano.
function ehBotaoPagamento(botao) {
  const url = String(botao?.url || '')
  return /hiperpay/i.test(url) && /\{\{.*?\}\}/.test(url)
}

// Lista os {{n}} do corpo, na ordem em que aparecem, sem repetir.
export function variaveisDoTemplate(template) {
  const body = acharComponente(template?.components, 'BODY')
  const texto = String(body?.text || '')
  const achados = texto.match(/\{\{\s*(\d+)\s*\}\}/g) || []
  const numeros = achados.map((t) => Number(t.replace(/\D/g, '')))
  return [...new Set(numeros)]
}

export function montarSnippetTemplate(template, valores = []) {
  const nome = String(template?.name || template?.metaTemplateName || '').trim()
  if (!nome) return ''

  let out = `%WhatsappCloudApi%%Template%%Name%${nome}%/Name%`

  variaveisDoTemplate(template).forEach((n, i) => {
    const bruto = valores[i]
    // NUNCA emitir vazio: o parser troca variável vazia pela mensagem padrão da
    // instância ("Valor não preenchido pelo sistema integrado"), e isso chegaria
    // ao cliente final como se fosse o conteúdo.
    const valor = bruto != null && String(bruto).trim() !== ''
      ? String(bruto).trim()
      : `valor_da_variavel_${n}`
    out += `%VariableNumeric%${valor}%/VariableNumeric%`
  })

  const botoes = acharComponente(template?.components, 'BUTTONS')?.buttons || []
  botoes.forEach((botao) => {
    // QUICK_REPLY não entra: a posição é contada só entre botões de URL.
    if (String(botao?.type || '').toUpperCase().trim() !== 'URL') return
    const conteudo = ehBotaoPagamento(botao)
      ? TOKEN_LINK_PAGAMENTO
      : String(botao?.url || '').trim()
    out += `%ButtonUrl%${conteudo}%/ButtonUrl%`
  })

  return `${out}%/Template%%/WhatsappCloudApi%`
}

// Leitura de volta: ao EDITAR um agendamento Oficial, o que está salvo é o
// snippet, não um texto legível. Sem isto a tela abriria vazia e o cliente
// teria que reescolher o template e redigitar cada variável.
export function lerSnippetTemplate(texto) {
  const t = String(texto || '')
  const nome = (t.match(/%Name%([\s\S]*?)%\/Name%/) || [])[1]
  if (!nome) return null

  const valores = (t.match(/%VariableNumeric%[\s\S]*?%\/VariableNumeric%/g) || [])
    .map((bloco) => bloco.replace(/%\/?VariableNumeric%/g, '').trim())

  return { nome: nome.trim(), valores }
}

export function ehSnippetTemplate(texto) {
  return /%Template%/.test(String(texto || ''))
}

// Rótulo curto pra cada variável, tirado do texto imediatamente antes dela —
// "sua fatura de {{2}}" vira "fatura de". Sem isso o cliente vê "Variável 2" e
// não sabe o que preencher.
export function rotuloDaVariavel(template, n) {
  const body = acharComponente(template?.components, 'BODY')
  const texto = String(body?.text || '')
  const idx = texto.indexOf(`{{${n}}}`)
  if (idx < 0) return `Variável ${n}`

  const antes = texto.slice(0, idx)
  const corte = Math.max(
    antes.lastIndexOf('.'), antes.lastIndexOf('\n'), antes.lastIndexOf('}}')
  )
  const trecho = antes.slice(corte + 1).replace(/[{}]/g, '').trim()
  const palavras = trecho.split(/\s+/).filter(Boolean).slice(-3).join(' ')
  return palavras || `Variável ${n}`
}

// Resolve {{variavel}} com os dados de um destinatário. Sem valor, devolve
// [variavel] em vez de deixar a chave crua: numa prévia ou num envio de teste,
// "Olá {{nome}}" chegaria assim no WhatsApp e pareceria defeito.
export function aplicarVariaveis(texto, destinatario) {
  const d = destinatario || {}
  const vars = d.vars || {}
  const tabela = { nome: d.name, telefone: d.phone, ...vars }

  return String(texto || '').replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, chave) => {
    const valor = tabela[chave]
    return valor === undefined || valor === null || valor === '' ? `[${chave}]` : String(valor)
  })
}
